// src/features/services/agent/components/ProposalPreview.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Renders the AI-generated SKU + BOM proposal for user review.
// Shows proposed SKUs grouped by tier, BOM relationships, and
// confirm / reject actions. Supports inline editing with diff indicators.
// PAC Glass surfaces.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useCallback, useMemo } from 'react';
import type {
  Proposal,
  ProposedSku,
  ProposedBom,
  AgentThread,
  CreatedEntities,
  ConfirmPayload,
} from '../api/agentApi';

// ── Sub-components ───────────────────────────────────────────────────────

function SkuTypeBadge({ type }: { type: string }) {
  const cls = type === 'assembly'
    ? 'bg-purple-900/40 text-purple-400 border-purple-700/40'
    : 'bg-cyan-900/40 text-cyan-400 border-cyan-700/40';
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded border ${cls}`}>
      {type}
    </span>
  );
}

function SkuCard({ sku, isDuplicate }: { sku: ProposedSku; isDuplicate?: boolean }) {
  return (
    <div className={`pac-glass-subtle rounded p-3 border space-y-2 ${isDuplicate ? 'border-amber-700/30' : 'border-white/5'}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-sm text-purple-400">{sku.sku}</span>
        <SkuTypeBadge type={sku.sku_type} />
        <span className="text-[10px] text-neutral-600">tier {sku.tier}</span>
        {isDuplicate && (
          <span className="text-[9px] text-amber-400 bg-amber-900/20 px-1 py-0.5 rounded border border-amber-700/30">
            exists
          </span>
        )}
      </div>

      <p className="text-sm text-neutral-200">{sku.label}</p>
      <p className="text-xs text-neutral-500">{sku.description}</p>

      <div className="flex items-center gap-4 text-[10px] text-neutral-600">
        <span>category: <span className="text-neutral-400">{sku.category}</span></span>
        {sku.parent_sku && (
          <span>parent: <span className="text-neutral-400 font-mono">{sku.parent_sku}</span></span>
        )}
        {sku.unit_cost != null && (
          <span>cost: <span className="text-emerald-400">{sku.unit_cost} {sku.currency ?? 'USD'}</span></span>
        )}
      </div>

      {sku.tags.length > 0 && (
        <div className="flex gap-1 flex-wrap">
          {sku.tags.map(tag => (
            <span key={tag} className="px-1.5 py-0.5 text-[9px] rounded bg-white/[0.04] text-neutral-500 border border-white/5">
              {tag}
            </span>
          ))}
        </div>
      )}

      {sku.hardware_spec && Object.keys(sku.hardware_spec).length > 0 && (
        <div className="flex gap-x-4 gap-y-1 flex-wrap text-[10px]">
          {Object.entries(sku.hardware_spec).map(([k, v]) => (
            <span key={k} className="text-neutral-600">
              {k}: <span className="text-neutral-400">{String(v)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Inline-editable SKU field ────────────────────────────────────────────

const EDIT_INPUT_CLS =
  'pac-glass-input w-full rounded px-2 py-1 text-sm text-neutral-200 ' +
  'bg-white/[0.04] border border-cyan-700/40 focus:border-cyan-500/60 ' +
  'focus:ring-1 focus:ring-cyan-500/30 outline-none';

function DiffDot() {
  return <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-400 ml-1" title="edited" />;
}

interface EditableSkuCardProps {
  original: ProposedSku;
  edited: Partial<ProposedSku>;
  isDuplicate?: boolean;
  onChange: (field: string, value: unknown) => void;
}

function EditableSkuCard({ original, edited, isDuplicate, onChange }: EditableSkuCardProps) {
  const merged = { ...original, ...edited };
  const isChanged = (f: string) => edited[f as keyof ProposedSku] !== undefined;

  return (
    <div className={`pac-glass-subtle rounded p-3 border space-y-2 ${isDuplicate ? 'border-amber-700/30' : 'border-cyan-900/30'}`}>
      {/* SKU code (read-only identity) + type toggle */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-sm text-purple-400">{original.sku}</span>
        <select
          value={merged.sku_type}
          onChange={e => onChange('sku_type', e.target.value)}
          className="px-2 py-0.5 text-[10px] rounded bg-white/[0.04] border border-cyan-700/40
                     text-neutral-300 outline-none"
        >
          <option value="component">component</option>
          <option value="assembly">assembly</option>
        </select>
        {isChanged('sku_type') && <DiffDot />}
        <span className="text-[10px] text-neutral-600">tier {merged.tier}</span>
        {isDuplicate && (
          <span className="text-[9px] text-amber-400 bg-amber-900/20 px-1 py-0.5 rounded border border-amber-700/30">
            exists
          </span>
        )}
      </div>

      {/* Label */}
      <div>
        <label className="text-[9px] text-neutral-600 uppercase tracking-wider">
          Label{isChanged('label') && <DiffDot />}
        </label>
        <input
          value={merged.label}
          onChange={e => onChange('label', e.target.value)}
          className={EDIT_INPUT_CLS}
        />
      </div>

      {/* Description */}
      <div>
        <label className="text-[9px] text-neutral-600 uppercase tracking-wider">
          Description{isChanged('description') && <DiffDot />}
        </label>
        <textarea
          value={merged.description}
          onChange={e => onChange('description', e.target.value)}
          rows={2}
          className={`${EDIT_INPUT_CLS} resize-none`}
        />
      </div>

      {/* Category + Cost row */}
      <div className="flex gap-3">
        <div className="flex-1">
          <label className="text-[9px] text-neutral-600 uppercase tracking-wider">
            Category{isChanged('category') && <DiffDot />}
          </label>
          <input
            value={merged.category}
            onChange={e => onChange('category', e.target.value)}
            className={EDIT_INPUT_CLS}
          />
        </div>
        <div className="w-24">
          <label className="text-[9px] text-neutral-600 uppercase tracking-wider">
            Cost{isChanged('unit_cost') && <DiffDot />}
          </label>
          <input
            type="number"
            step="0.01"
            value={merged.unit_cost ?? ''}
            onChange={e => onChange('unit_cost', e.target.value ? Number(e.target.value) : null)}
            className={EDIT_INPUT_CLS}
            placeholder="—"
          />
        </div>
        <div className="w-16">
          <label className="text-[9px] text-neutral-600 uppercase tracking-wider">
            Cur{isChanged('currency') && <DiffDot />}
          </label>
          <input
            value={merged.currency ?? ''}
            onChange={e => onChange('currency', e.target.value || null)}
            className={EDIT_INPUT_CLS}
            placeholder="USD"
            maxLength={3}
          />
        </div>
      </div>

      {/* Tags */}
      <div>
        <label className="text-[9px] text-neutral-600 uppercase tracking-wider">
          Tags (comma-separated){isChanged('tags') && <DiffDot />}
        </label>
        <input
          value={(merged.tags ?? []).join(', ')}
          onChange={e => onChange('tags', e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
          className={EDIT_INPUT_CLS}
          placeholder="iot, esp32, sensor"
        />
      </div>
    </div>
  );
}

function BomCard({ bom }: { bom: ProposedBom }) {
  return (
    <div className="pac-glass-subtle rounded p-3 border border-white/5 space-y-2">
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm text-emerald-400">{bom.finished_sku}</span>
        <span className="text-[10px] text-neutral-600">v{bom.version}</span>
      </div>
      <p className="text-xs text-neutral-300">{bom.label}</p>

      <div className="space-y-1">
        {bom.components.map((c, i) => (
          <div key={i} className="flex items-center gap-3 text-xs">
            <span className="text-emerald-400 w-6 text-right font-mono">{c.quantity}×</span>
            <span className="font-mono text-neutral-300">{c.sku}</span>
            <span className="text-neutral-500">({c.unit})</span>
            <span className="text-neutral-600">{c.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CreatedEntitiesSummary({ entities }: { entities: CreatedEntities }) {
  const skusOk = entities.skus.filter(s => s.status === 'created').length;
  const skusFail = entities.skus.filter(s => s.status === 'failed').length;
  const bomsOk = entities.boms.filter(b => b.status === 'created').length;
  const bomsFail = entities.boms.filter(b => b.status === 'failed').length;

  return (
    <div className="pac-glass-subtle rounded p-3 border border-emerald-900/30 space-y-2">
      <p className="text-[10px] text-neutral-500 uppercase tracking-wider">Created Entities</p>
      <div className="flex gap-4 text-xs">
        <span className="text-emerald-400">{skusOk} SKU(s) created</span>
        {skusFail > 0 && <span className="text-rose-400">{skusFail} SKU(s) failed</span>}
        <span className="text-emerald-400">{bomsOk} BOM(s) created</span>
        {bomsFail > 0 && <span className="text-rose-400">{bomsFail} BOM(s) failed</span>}
      </div>

      {/* Show individual failures */}
      {[...entities.skus, ...entities.boms]
        .filter(e => e.status === 'failed')
        .map((e, i) => (
          <p key={i} className="text-[10px] text-rose-400">
            {e.sku ?? e.bom_key}: {e.error}
          </p>
        ))}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────

interface ProposalPreviewProps {
  thread: AgentThread;
  onConfirm: (payload?: ConfirmPayload) => void;
  onReject: () => void;
  isConfirming: boolean;
  isRejecting: boolean;
}

export default function ProposalPreview({
  thread,
  onConfirm,
  onReject,
  isConfirming,
  isRejecting,
}: ProposalPreviewProps) {
  const proposal = thread.proposal;
  const isPending = thread.status === 'pending';

  // ── Edit state ─────────────────────────────────────────────────────────
  const [isEditing, setIsEditing] = useState(false);
  // Map<sku_code, Partial<ProposedSku>> — only changed fields
  const [overrides, setOverrides] = useState<Record<string, Partial<ProposedSku>>>({});

  const editCount = useMemo(() => {
    return Object.values(overrides).reduce(
      (sum, o) => sum + Object.keys(o).length,
      0,
    );
  }, [overrides]);

  const handleSkuChange = useCallback(
    (sku: string, field: string, value: unknown) => {
      setOverrides(prev => {
        const original = proposal?.skus.find(s => s.sku === sku);
        if (!original) return prev;

        const existing = prev[sku] ?? {};
        // If value reverts to original, remove the override
        const origVal = original[field as keyof ProposedSku];
        const same = JSON.stringify(value) === JSON.stringify(origVal);

        const updated = { ...existing };
        if (same) {
          delete updated[field as keyof Partial<ProposedSku>];
        } else {
          (updated as any)[field] = value;
        }

        // Clean up empty override entries
        if (Object.keys(updated).length === 0) {
          const { [sku]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [sku]: updated };
      });
    },
    [proposal],
  );

  const handleConfirmWithOverrides = useCallback(() => {
    if (editCount > 0) {
      const skuOverrides = Object.entries(overrides).map(([sku, fields]) => ({
        sku,
        ...fields,
      }));
      onConfirm({ overrides: { skus: skuOverrides as Partial<ProposedSku>[] } });
    } else {
      onConfirm();
    }
  }, [editCount, overrides, onConfirm]);

  const handleCancelEdit = useCallback(() => {
    setOverrides({});
    setIsEditing(false);
  }, []);

  if (thread.status === 'processing') {
    return (
      <div className="pac-glass-raised rounded p-4 border border-purple-900/30 space-y-2">
        <p className="text-sm text-purple-400 font-semibold animate-pulse">AI Processing…</p>
        <p className="text-xs text-neutral-500">
          The pipeline is running. Progress appears above. This preview will update automatically.
        </p>
      </div>
    );
  }

  if (thread.status === 'failed') {
    return (
      <div className="pac-glass-raised rounded p-4 border border-rose-900/30 space-y-2">
        <p className="text-sm text-rose-400 font-semibold">AI Processing Failed</p>
        <p className="text-xs text-neutral-400">{thread.error_detail}</p>
        <p className="text-[10px] text-neutral-600">
          Description: {thread.description.slice(0, 200)}{thread.description.length > 200 ? '…' : ''}
        </p>
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="pac-glass-raised rounded p-4 border border-neutral-800">
        <p className="text-xs text-neutral-500">No proposal data available.</p>
      </div>
    );
  }

  const assemblies = proposal.skus.filter(s => s.sku_type === 'assembly');
  const components = proposal.skus.filter(s => s.sku_type === 'component');
  const duplicates = thread.duplicate_matches;
  const dupCount = duplicates ? Object.keys(duplicates).length : 0;

  return (
    <div className="pac-glass-raised rounded p-4 border border-neutral-800 space-y-5">
      {/* Header */}
      <div className="space-y-1">
        <h3 className="text-sm font-semibold text-neutral-200">{proposal.assembly_name}</h3>
        <p className="text-xs text-neutral-400">{proposal.summary}</p>
        <div className="flex gap-4 text-[10px] text-neutral-600">
          <span>{proposal.skus.length} SKU(s)</span>
          <span>{proposal.boms?.length ?? 0} BOM(s)</span>
          <span>Model: {thread.ai_model ?? 'unknown'}</span>
        </div>
      </div>

      {/* Duplicate warning */}
      {dupCount > 0 && (
        <div className="pac-glass-subtle rounded p-3 border border-amber-900/30 space-y-1">
          <p className="text-xs text-amber-400 font-semibold">
            ⚠ {dupCount} SKU(s) already exist in the catalog
          </p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(duplicates!).map(([sku, info]) => (
              <span key={sku} className="text-[10px] font-mono text-amber-400 bg-amber-900/20 px-1.5 py-0.5 rounded border border-amber-700/30">
                {sku}
                <span className="text-neutral-500 ml-1">({info.label})</span>
              </span>
            ))}
          </div>
          <p className="text-[10px] text-neutral-500">
            Confirming will update existing SKUs rather than creating new ones.
          </p>
        </div>
      )}

      {/* Assemblies */}
      {assemblies.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] text-neutral-500 uppercase tracking-wider">
            Assemblies (Tier 0)
          </p>
          <div className="grid gap-2">
            {assemblies.map(s =>
              isEditing ? (
                <EditableSkuCard
                  key={s.sku}
                  original={s}
                  edited={overrides[s.sku] ?? {}}
                  isDuplicate={!!duplicates?.[s.sku]}
                  onChange={(f, v) => handleSkuChange(s.sku, f, v)}
                />
              ) : (
                <SkuCard key={s.sku} sku={s} isDuplicate={!!duplicates?.[s.sku]} />
              ),
            )}
          </div>
        </div>
      )}

      {/* Components */}
      {components.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] text-neutral-500 uppercase tracking-wider">
            Components
          </p>
          <div className="grid gap-2">
            {components.map(s =>
              isEditing ? (
                <EditableSkuCard
                  key={s.sku}
                  original={s}
                  edited={overrides[s.sku] ?? {}}
                  isDuplicate={!!duplicates?.[s.sku]}
                  onChange={(f, v) => handleSkuChange(s.sku, f, v)}
                />
              ) : (
                <SkuCard key={s.sku} sku={s} isDuplicate={!!duplicates?.[s.sku]} />
              ),
            )}
          </div>
        </div>
      )}

      {/* BOMs */}
      {proposal.boms && proposal.boms.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] text-neutral-500 uppercase tracking-wider">
            Bills of Materials
          </p>
          <div className="grid gap-2">
            {proposal.boms.map((b, i) => <BomCard key={i} bom={b} />)}
          </div>
        </div>
      )}

      {/* Image Assignments */}
      {thread.image_assignments && thread.image_assignments.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] text-neutral-500 uppercase tracking-wider">
            Image → Product Mapping
          </p>
          <div className="grid gap-2">
            {thread.image_assignments.map((ia, i) => (
              <div key={i} className="pac-glass-subtle rounded px-3 py-2 border border-white/5 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-neutral-400">
                    Image {ia.image_index + 1}
                    {ia.filename && <span className="text-neutral-600 ml-1">({ia.filename})</span>}
                  </span>
                  {ia.quarantine_uuid && (
                    <span className="text-[9px] text-emerald-600 bg-emerald-900/20 px-1 py-0.5 rounded">
                      quarantined
                    </span>
                  )}
                  {ia.sha256_short && (
                    <span className="text-[9px] text-neutral-600 font-mono">{ia.sha256_short}</span>
                  )}
                  <span className="text-[10px] text-neutral-600">→</span>
                  <div className="flex gap-1 flex-wrap">
                    {ia.assigned_skus.map(sku => (
                      <span key={sku} className="font-mono text-[10px] text-purple-400 bg-purple-900/20 px-1.5 py-0.5 rounded">
                        {sku}
                      </span>
                    ))}
                  </div>
                </div>
                {ia.description && (
                  <p className="text-[10px] text-neutral-500">{ia.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Telemetry */}
      {(thread.ai_duration_ms || thread.total_duration_ms || thread.audio_quarantine) && (
        <div className="flex gap-4 flex-wrap text-[10px] text-neutral-600 border-t border-white/5 pt-2">
          {thread.trace_id && (
            <span className="font-mono text-neutral-700" title={thread.trace_id}>
              trace:{thread.trace_id.slice(0, 8)}
            </span>
          )}
          {thread.ai_duration_ms != null && (
            <span>AI: {(thread.ai_duration_ms / 1000).toFixed(1)}s</span>
          )}
          {thread.total_duration_ms != null && (
            <span>Total: {(thread.total_duration_ms / 1000).toFixed(1)}s</span>
          )}
          {thread.token_estimate != null && (
            <span>~{thread.token_estimate.toLocaleString()} tokens</span>
          )}
          {thread.voice_input && (
            <span className="text-purple-400">🎤 Voice input</span>
          )}
          {thread.audio_quarantine?.uuid && (
            <span className="text-emerald-600 bg-emerald-900/20 px-1 py-0.5 rounded">
              🎤 audio archived
              <span className="font-mono ml-1 text-neutral-600">{thread.audio_quarantine.sha256_short}</span>
            </span>
          )}
        </div>
      )}

      {/* Created entities summary (for confirmed threads) */}
      {thread.status === 'confirmed' && thread.created_entities && (
        <CreatedEntitiesSummary entities={thread.created_entities} />
      )}

      {/* Actions (only for pending threads) */}
      {isPending && (
        <div className="space-y-2 pt-2 border-t border-white/5">
          {/* Edit mode toggle + diff summary */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsEditing(e => !e)}
              disabled={isConfirming || isRejecting}
              className="pac-glass-btn text-xs px-3 py-1.5 rounded border border-cyan-500/30
                         text-cyan-400 hover:border-cyan-400 transition-colors
                         disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isEditing ? 'Done Editing' : '✏ Edit SKUs'}
            </button>
            {isEditing && editCount > 0 && (
              <>
                <span className="text-[10px] text-cyan-400">
                  {editCount} field(s) modified
                </span>
                <button
                  onClick={handleCancelEdit}
                  className="text-[10px] text-neutral-500 hover:text-neutral-300 transition-colors"
                >
                  Reset all
                </button>
              </>
            )}
          </div>

          {/* Confirm / Reject */}
          <div className="flex gap-3">
            <button
              onClick={handleConfirmWithOverrides}
              disabled={isConfirming || isRejecting}
              className="pac-glass-btn px-4 py-2 rounded text-sm border border-emerald-500/40
                         text-emerald-400 hover:border-emerald-400 transition-colors
                         disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isConfirming
                ? 'Creating entities…'
                : editCount > 0
                  ? `Confirm with ${editCount} edit(s)`
                  : 'Confirm & Create All'}
            </button>
            <button
              onClick={onReject}
              disabled={isConfirming || isRejecting}
              className="pac-glass-btn px-3 py-2 rounded text-sm border border-rose-500/40
                         text-rose-400 hover:border-rose-400 transition-colors
                         disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isRejecting ? 'Rejecting…' : 'Discard'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
